import { BN } from '@coral-xyz/anchor';
import { useSignTransaction } from '@privy-io/react-auth/solana';
import { getAssociatedTokenAddress, TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { useConnection } from '@solana/wallet-adapter-react';
import {
  PublicKey,
  Keypair,
  Transaction,
  VersionedTransaction,
  TransactionInstruction,
  ComputeBudgetProgram,
  Connection,
} from '@solana/web3.js';
import { getSimulationComputeUnits } from '@solana-developers/helpers';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ToastProgressBar } from '@/components/ui/toast-progressbar';
import { useFetchAccount } from '@/features/account/api/get-account';
import { getTransfers } from '@/features/account/api/get-transfers';
import { usePrivyAccount } from '@/hooks';
import { USDC_MINT_ADDRESS, MINT_RECIPIENT } from '@/lib/wagmi/config';
import { useAccountStore } from '@/store/use-account-store';
import { COLLATERAL_DECIMALS } from '@/store/use-markets-store';
import { useTransfersStore } from '@/store/use-transfers-store';
import { EventType, ErrorCode, ErrorMsgs, OrderStatus } from '@/types';
import { sendEvent } from '@/utils/analytics';
import {
  getDepositForBurnPdas,
  getPrioritizationFees,
  getPrograms,
} from '@/utils/solana';
import { bigIntToNum, parseDecimalToBigInt } from '@/utils/value-format';

import { useTransferDepositSolana } from './transfer-deposit-solana';

const MEMO_PROGRAM_ID = new PublicKey(
  'MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr',
);

export default function useSolanaDeposit() {
  const { t } = useTranslation();
  const { connection } = useConnection();
  const { data: account } = useFetchAccount();
  const { mutateAsync: transferDepositSolana } = useTransferDepositSolana();
  const { email, solanaWallets } = usePrivyAccount();
  const { signTransaction } = useSignTransaction();

  const address = useAccountStore((state) => state.address);

  const wallet = useMemo(
    () => solanaWallets.find((w) => w.address === address),
    [address, solanaWallets],
  );

  const depositForBurn = useCallback(
    async (decimalSize: string) => {
      if (!address || !wallet) {
        toast.error('Wallet not connected. Please connect your wallet.');
        return;
      }

      if (!account?.address) {
        toast.error('Wallet address not found. Please reconnect your wallet.');
        return;
      }

      const publicKey = new PublicKey(address);
      const size = parseDecimalToBigInt(
        decimalSize.endsWith('.') ? `${decimalSize}00` : decimalSize,
        COLLATERAL_DECIMALS,
      );

      try {
        const provider = {
          connection,
          wallet: {
            publicKey,
            signTransaction: async (tx: Transaction) => {
              return await signTransaction({
                transaction: tx,
                address,
                connection,
                uiOptions: { showWalletUIs: false },
              });
            },
            signAllTransactions: async (txs: Transaction[]) => {
              return await Promise.all(
                txs.map((tx) =>
                  signTransaction({
                    transaction: tx,
                    address,
                    connection,
                    uiOptions: { showWalletUIs: false },
                  }),
                ),
              );
            },
          },
        };

        const { messageTransmitterProgram, tokenMessengerMinterProgram } =
          getPrograms(provider);

        const usdcAddress = new PublicKey(USDC_MINT_ADDRESS);

        const userTokenAccount = await getAssociatedTokenAddress(
          new PublicKey(USDC_MINT_ADDRESS),
          publicKey,
        );

        const destinationDomain = 3;
        const amount = new BN(size);
        const mintRecipientHex = `0x000000000000000000000000${MINT_RECIPIENT.substring(2)}`;
        const mintRecipient = new PublicKey(
          Buffer.from(mintRecipientHex.slice(2), 'hex'),
        );

        const pdas = getDepositForBurnPdas(
          { messageTransmitterProgram, tokenMessengerMinterProgram },
          usdcAddress,
          destinationDomain,
        );

        // Generate a new keypair for the MessageSent event account.
        const messageSentEventAccountKeypair = Keypair.generate();

        const memoInstruction = new TransactionInstruction({
          keys: [{ pubkey: publicKey, isSigner: true, isWritable: true }],
          programId: MEMO_PROGRAM_ID,
          data: Buffer.from(account?.address, 'utf-8'),
        });

        const instruction = await tokenMessengerMinterProgram.methods
          .depositForBurn({ amount, destinationDomain, mintRecipient })
          .accounts({
            owner: provider.wallet.publicKey,
            eventRentPayer: provider.wallet.publicKey,
            senderAuthorityPda: pdas.authorityPda.publicKey,
            burnTokenAccount: userTokenAccount,
            messageTransmitter: pdas.messageTransmitterAccount.publicKey,
            tokenMessenger: pdas.tokenMessengerAccount.publicKey,
            remoteTokenMessenger: pdas.remoteTokenMessengerKey.publicKey,
            tokenMinter: pdas.tokenMinterAccount.publicKey,
            localToken: pdas.localToken.publicKey,
            burnTokenMint: usdcAddress,
            messageTransmitterProgram: messageTransmitterProgram.programId,
            tokenMessengerMinterProgram: tokenMessengerMinterProgram.programId,
            messageSentEventData: messageSentEventAccountKeypair.publicKey,
            tokenProgram: TOKEN_PROGRAM_ID,
          })
          .instruction();

        let units: number = 270;
        try {
          units =
            (await getSimulationComputeUnits(
              connection,
              [instruction, memoInstruction],
              publicKey,
              [],
            )) ?? 270;
        } catch (error) {
          console.error('Compute units error', error);
        }

        const modifyComputeUnits = ComputeBudgetProgram.setComputeUnitLimit({
          units: Math.ceil(units * 1.1),
        });
        const microLamports = await getPrioritizationFees(connection);
        // Call depositForBurn
        const addPriorityFee = ComputeBudgetProgram.setComputeUnitPrice({
          microLamports,
        });

        const { blockhash, lastValidBlockHeight } =
          await connection.getLatestBlockhash('finalized');

        const transaction = new Transaction();
        transaction.recentBlockhash = blockhash;
        transaction.lastValidBlockHeight = lastValidBlockHeight;
        transaction.feePayer = publicKey;

        transaction.add(
          modifyComputeUnits,
          addPriorityFee,
          instruction,
          memoInstruction,
        );

        const walletSigned = await signTransaction({
          transaction: transaction,
          address,
          connection,
          uiOptions: { showWalletUIs: false },
        });

        let raw: Uint8Array;

        if (walletSigned instanceof Transaction) {
          // legacy tx
          walletSigned.partialSign(messageSentEventAccountKeypair);
          raw = walletSigned.serialize();
        } else if (walletSigned instanceof VersionedTransaction) {
          // v0 tx
          walletSigned.sign([messageSentEventAccountKeypair]);
          raw = walletSigned.serialize();
        } else {
          throw new Error('Unsupported Solana transaction type');
        }

        const sig = await connection.sendRawTransaction(raw, {
          skipPreflight: false,
          preflightCommitment: 'processed',
        });

        transferDepositSolana(sig);

        const loadingToast = toast.custom(
          () => (
            <ToastProgressBar
              message={t('createDeposit.depositing')}
              limit={30000}
            />
          ),
          { duration: Infinity },
        );

        const subscriptionConnection = new Connection(
          import.meta.env.VITE_SOLANA_RPC,
          {
            wsEndpoint: import.meta.env.VITE_SOLANA_WS_RPC,
          },
        );

        await subscriptionConnection.confirmTransaction(
          { blockhash, lastValidBlockHeight, signature: sig },
          'processed',
        );

        // const promise = Promise.resolve(sig)
        //   .then((depositForBurnTx) => {
        sendEvent(EventType.DEPOSIT_COMPLETED, {
          amount: bigIntToNum(size, COLLATERAL_DECIMALS),
          email,
        });

        const confirmPromise = new Promise((resolve, reject) => {
          try {
            const time = useAccountStore.getState().getAccurateTime();

            // eslint-disable-next-line prefer-const
            let subscriberTimeoutId: NodeJS.Timeout;
            // eslint-disable-next-line prefer-const
            let pollIntervalId: NodeJS.Timeout;
            // eslint-disable-next-line prefer-const
            let unsubscribe: () => void;

            const cleanup = () => {
              clearTimeout(subscriberTimeoutId);
              clearInterval(pollIntervalId);
              unsubscribe();
            };

            unsubscribe = useTransfersStore.subscribe((state) => {
              const newDeposits = state.newDeposits[address] || [];
              if (newDeposits.length > 0) {
                for (const deposit of newDeposits) {
                  if (
                    deposit.postTime >= time &&
                    ('size' in deposit ? deposit.size === size : true)
                  ) {
                    state.removeNewDeposit(deposit.id, address);
                    if (deposit.status === OrderStatus.FILLED) {
                      cleanup();
                      resolve(deposit);
                    } else if (deposit.status === OrderStatus.REJECTED) {
                      cleanup();
                      reject(deposit.code as ErrorCode);
                    }
                  }
                }
              }
            });

            subscriberTimeoutId = setTimeout(() => {
              cleanup();
              reject(ErrorCode.INTERNAL_CREATE_ORDER_TIMEOUT);
            }, 120_000);

            pollIntervalId = setInterval(() => {
              getTransfers({ startTime: time });
            }, 2_000);
          } catch (error) {
            console.error('Error during transferDepositSolana:', error);
            reject(error as ErrorCode);
          }
        });

        confirmPromise.then(() =>
          toast.success(`${t('createDeposit.deposited')} ${decimalSize} USD`),
        );

        confirmPromise.catch((error) =>
          toast.error(
            `${t('createDeposit.depositFailed')} ${
              ErrorMsgs[error as ErrorCode] ?? t('createDeposit.unhandledError')
            }`,
          ),
        );

        confirmPromise.finally(() => toast.dismiss(loadingToast));

        // })
        // .catch((error) => {
        //   const errorMessage =
        //     error?.message ||
        //     (typeof error === 'string'
        //       ? error
        //       : t('createDeposit.unhandledError'));

        //   toast.error(
        //     errorMessage.includes('not connected')
        //       ? t('createDeposit.walletNotConnected')
        //       : errorMessage,
        //   );
        // });
      } catch (error) {
        console.error('Error during depositForBurn:', error);
        toast.error(
          `${t('createDeposit.depositFailed')} Please try again or contact support.`,
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [connection, account?.address, wallet, address, email],
  );

  return { depositForBurn };
}
